import { IDynamicReportEntry } from '@/types/report'

/**
 * Dynamic Report Attribute IDs
 *
 * These are predefined attribute IDs used by the dynamic-report API endpoint.
 * Each report type has specific attributes that determine what data is returned.
 *
 * API Endpoint: POST /api/report/dynamic-report
 *
 * @see SCRUM-742 for migration details
 */
export const DYNAMIC_REPORT_ATTRIBUTES = {
  FLOW_LOG: {
    EXPECTED_FLOW: '24', // Response field: florulIExpFloDlMin
    MEASURED_FLOW: '25', // Response field: florulIMeasFloDlMin
    UNEXPECTED_FLOW: '78', // Response field: florulIUnexpFloDlMin
  },
  FLOW_VOLUME: {
    EXPECTED_VOLUME: '18', // Response field: florulIexpVol_dl (with period: 'hour')
    MEASURED_VOLUME: '17', // Response field: florulImeasVol_dl (with period: 'hour')
    UNEXPECTED_VOLUME: '77', // Response field: florulIunexpVol_dl (with period: 'hour')
  },
  PROGRAM_START: {
    PROGRAM_STATE: '27', // Response fields: progId, progState
  },
  STATION_RUNTIME: {
    RUNTIME: '21', // Response field: staRuntimeSec
    STATION_START: '32', // Response field: progId, ioAddrIoIdx
  },
  MONTHLY_RAINFALL: {
    RAIN_VOLUME: '20', // Response field: rainVolDum
  },
} as const

/**
 * Pre-configured entries for Flow Log report
 * Used by: FlowLogSlice, FLowLog/index.tsx (export)
 */
export const FLOW_LOG_ENTRIES: IDynamicReportEntry[] = [
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_LOG.EXPECTED_FLOW },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_LOG.MEASURED_FLOW },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_LOG.UNEXPECTED_FLOW },
]

/**
 * Pre-configured entries for Program Start report
 * Used by: useProgramStart hook
 */
export const PROGRAM_START_ENTRIES: IDynamicReportEntry[] = [
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.PROGRAM_START.PROGRAM_STATE },
]

/**
 * Pre-configured entries for Station Runtime report
 * Used by: useSearchStationRunTimeDynamic hook (single API call)
 *
 * Attributes [21, 32, 27]:
 * - Runtime records (attr 21): staId, staRuntimeSec, pAWP
 * - Station START records (attr 32): staId, ioAddrIoIdx, progId
 * - Program state records (attr 27): progId, progState (global, staId = null)
 *
 * Manual vs Automatic detection logic:
 * 1. Filter runtime records with pAWP = "0" (actual stop events)
 * 2. For each runtime event, find Station START record nearest before (same staId)
 * 3. Find progState nearest before that START record's timestamp
 * 4. If progState = "manual" -> Manual, otherwise -> Automatic
 */
export const STATION_RUNTIME_ENTRIES: IDynamicReportEntry[] = [
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.STATION_RUNTIME.RUNTIME },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.STATION_RUNTIME.STATION_START },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.PROGRAM_START.PROGRAM_STATE },
]

/**
 * Pre-configured entries for Monthly Rainfall report
 * Used by: (TODO) MonthlyRainfallSlice
 * Note: period='hour' is required for this report
 */
export const MONTHLY_RAINFALL_ENTRIES: IDynamicReportEntry[] = [
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.MONTHLY_RAINFALL.RAIN_VOLUME, period: 'hour' },
]

/**
 * Pre-configured entries for Flow Volume report (Calculated Water Use)
 * Used by: FlowLogReport page
 * Note: period='hour' is required for aggregated volume data
 * Maps from old attributes [143, 144, 145] to new [18, 17, 77]
 */
export const FLOW_VOLUME_ENTRIES: IDynamicReportEntry[] = [
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_VOLUME.EXPECTED_VOLUME, period: 'hour' },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_VOLUME.MEASURED_VOLUME, period: 'hour' },
  { attribute: DYNAMIC_REPORT_ATTRIBUTES.FLOW_VOLUME.UNEXPECTED_VOLUME, period: 'hour' },
]
